import React, { useState, useRef } from 'react';
import { Play, CheckCircle2, Award, Activity, BarChart3, Shield, Phone, MapPin, Mail, Send, Loader2, X, Maximize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

// Custom Video Player with proper fullscreen modal
const VideoPlayer = ({ src, poster, className }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);
  const fullscreenVideoRef = useRef(null);

  const openFullscreen = () => {
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    if (fullscreenVideoRef.current) {
      fullscreenVideoRef.current.pause();
    }
  };

  return (
    <>
      <div className={`relative group ${className || ''}`}>
        <video
          ref={videoRef}
          src={src}
          controls
          className="w-full h-full object-contain bg-black"
          poster={poster}
          playsInline
        >
          Your browser does not support the video tag.
        </video>
        <button
          onClick={openFullscreen}
          className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
          title="Open in fullscreen"
        >
          <Maximize2 size={20} />
        </button>
      </div>

      {isFullscreen && (
        <div 
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={closeFullscreen}
        >
          <button
            onClick={closeFullscreen}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white z-50 transition-colors"
          >
            <X size={28} />
          </button>
          
          <div 
            className="w-full h-full max-w-[177.78vh] max-h-[56.25vw] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              ref={fullscreenVideoRef}
              src={src}
              controls
              autoPlay
              className="w-full h-full object-contain"
              poster={poster}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}
    </>
  );
};

const ContractManufacturing = () => {
  return (
    <section id="manufacturing" className="py-24 bg-slate-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Contract <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">Manufacturing</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Six Sigma capable manufacturing facility with high-frequency data logging and rigorous testing protocols
          </p>
        </div>

        {/* Manufacturing Video */}
        <div className="mb-16">
          <Card className="bg-white border-2 border-slate-200 overflow-hidden hover:border-cyan-400 hover:shadow-xl transition-all">
            <div className="aspect-video bg-slate-900 relative">
              <VideoPlayer
                src="https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/8ai69aiu_Manufacturing%20video%20final%20Compressed.mp4"
                poster=""
                className="w-full h-full"
              />
            </div>
          </Card>
        </div>

        {/* Manufacturing Capabilities */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <Card className="bg-white/50 border-slate-200 p-6 hover:border-cyan-500/50 transition-all duration-300">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <Award className="text-cyan-600" size={32} />
              </div>
              <h4 className="text-slate-900 font-semibold">Six Sigma Capable</h4>
              <p className="text-slate-600 text-sm">World-class quality management system ensuring consistent excellence</p>
            </div>
          </Card>

          <Card className="bg-white/50 border-slate-200 p-6 hover:border-cyan-500/50 transition-all duration-300">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <Activity className="text-cyan-600" size={32} />
              </div>
              <h4 className="text-slate-900 font-semibold">High Frequency Data Logging</h4>
              <p className="text-slate-600 text-sm">Real-time monitoring and recording of every manufacturing parameter</p>
            </div>
          </Card>

          <Card className="bg-white/50 border-slate-200 p-6 hover:border-cyan-500/50 transition-all duration-300">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <BarChart3 className="text-cyan-600" size={32} />
              </div>
              <h4 className="text-slate-900 font-semibold">End of Line Testing</h4>
              <p className="text-slate-600 text-sm">Comprehensive testing with detailed data output for each unit</p>
            </div>
          </Card>

          <Card className="bg-white/50 border-slate-200 p-6 hover:border-cyan-500/50 transition-all duration-300">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <Shield className="text-cyan-600" size={32} />
              </div>
              <h4 className="text-slate-900 font-semibold">Pressure Decay Testing</h4>
              <p className="text-slate-600 text-sm">Rigorous leak testing ensuring zero-defect delivery</p>
            </div>
          </Card>
        </div>

        {/* Testing Data Placeholders */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* High Frequency End of Production Line Test */}
          <Card className="bg-white/50 border-slate-200 overflow-hidden hover:border-cyan-500/50 transition-all duration-300">
            <div className="p-6 border-b border-slate-200">
              <h4 className="text-xl font-bold text-slate-900">High Frequency End of Production Line Test</h4>
              <p className="text-slate-600 text-sm mt-2">Detailed data output and performance validation</p>
            </div>
            <div className="bg-slate-50 flex items-center justify-center p-8" style={{ minHeight: '480px' }}>
              <img
                src="https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/24feosq7_gui.png"
                alt="High Frequency End of Production Line Test Data"
                className="w-full h-full object-contain"
                style={{ maxHeight: '450px' }}
              />
            </div>
          </Card>

          {/* Pressure Decay Leak Testing */}
          <Card className="bg-white/50 border-slate-200 overflow-hidden hover:border-cyan-500/50 transition-all duration-300">
            <div className="p-6 border-b border-slate-200">
              <h4 className="text-xl font-bold text-slate-900">Pressure Decay Leak Testing</h4>
              <p className="text-slate-600 text-sm mt-2">Zero-tolerance leak detection system</p>
            </div>
            <div className="bg-slate-50 flex items-center justify-center p-8" style={{ minHeight: '480px' }}>
              <img
                src="https://customer-assets.emergentagent.com/job_fresh-compress/artifacts/90kqlg9v_A28_SENTINEL_HYS.png"
                alt="Pressure Decay Leak Testing Equipment"
                className="w-full h-full object-contain"
                style={{ maxHeight: '450px' }}
              />
            </div>
          </Card>
        </div>

        {/* Quality Assurance Points */}
        <div className="mt-12">
          <Card className="bg-white/50 border-slate-200 p-8">
            <h4 className="text-2xl font-bold text-slate-900 mb-6">Quality Assurance Highlights</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <p className="text-slate-900 font-semibold mb-1">100% Unit Testing</p>
                  <p className="text-slate-600 text-sm">Every dryer undergoes comprehensive performance validation before shipping</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <p className="text-slate-900 font-semibold mb-1">Data-Driven Quality Control</p>
                  <p className="text-slate-600 text-sm">High-frequency data logging ensures traceability and continuous improvement</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <p className="text-slate-900 font-semibold mb-1">Zero-Defect Philosophy</p>
                  <p className="text-slate-600 text-sm">Rigorous pressure decay leak testing guarantees reliable operation</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <p className="text-slate-900 font-semibold mb-1">Six Sigma Certified Process</p>
                  <p className="text-slate-600 text-sm">Industry-leading manufacturing standards ensuring consistent excellence</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${API_URL}/api/contact`, formData);
      
      toast({
        title: "Message Sent!",
        description: response.data.message || "Thank you for reaching out. We'll get back to you soon!",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "There was an issue sending your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Get in <span className="bg-gradient-to-r from-cyan-600 to-blue-700 bg-clip-text text-transparent">Touch</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Ready to experience the Sub-Zero difference? Contact us today for more information or to request a quote
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="bg-slate-50/50 border-slate-200 p-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex-shrink-0">
                    <Phone className="text-cyan-600" size={24} />
                  </div>
                  <div>
                    <p className="text-slate-600 text-sm mb-1">Phone</p>
                    <div className="flex flex-col">
                      <a
                        href="tel:+919894038894"
                        className="text-slate-900 text-lg font-semibold hover:text-cyan-600 transition-colors"
                      >
                        +91 98940 38894
                      </a>
                      <a
                        href="tel:+918870038894"
                        className="text-slate-900 text-lg font-semibold hover:text-cyan-600 transition-colors"
                      >
                        +91 88700 38894
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex-shrink-0">
                    <Mail className="text-cyan-600" size={24} />
                  </div>
                  <div>
                    <p className="text-slate-600 text-sm mb-1">Email</p>
                    <a
                      href="mailto:arun@atseng.in"
                      className="text-slate-900 text-lg font-semibold hover:text-cyan-600 transition-colors"
                    >
                      arun@atseng.in
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex-shrink-0">
                    <MapPin className="text-cyan-600" size={24} />
                  </div>
                  <div>
                    <p className="text-slate-600 text-sm mb-1">Address</p>
                    <div className="text-slate-900 leading-relaxed">
                      <p className="font-semibold">Pilot Factory (Sub-Zero Dryers)</p>
                      <p>SF246/3D Sriram Gardens</p>
                      <p>Maharani Ave 1st Ph, Vadavalli</p>
                      <p>Coimbatore 641041</p>
                      <p>TamilNadu, India</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Why Choose Us */}
            <Card className="bg-slate-50/50 border-slate-200 p-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Why Choose Sub-Zero?</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                  <p className="text-slate-700">Patent-pending Sub-Zero PDP technology (-10°C)</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                  <p className="text-slate-700">25% more energy efficient than competitors</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                  <p className="text-slate-700">Six Sigma capable manufacturing</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                  <p className="text-slate-700">100% unit testing with data logging</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="text-cyan-600 flex-shrink-0 mt-1" size={20} />
                  <p className="text-slate-700">Suitable for OEM and retrofit applications</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="bg-slate-50/50 border-slate-200 p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-white border-slate-700 text-slate-900 focus:border-cyan-500 focus:ring-amber-500"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-white border-slate-700 text-slate-900 focus:border-cyan-500 focus:ring-amber-500"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    Phone
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-white border-slate-700 text-slate-900 focus:border-cyan-500 focus:ring-amber-500"
                    placeholder="+91 xxxxx xxxxx"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                    Company
                  </label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    className="bg-white border-slate-700 text-slate-900 focus:border-cyan-500 focus:ring-amber-500"
                    placeholder="Company name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  Message *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  className="bg-white border-slate-700 text-slate-900 focus:border-cyan-500 focus:ring-amber-500"
                  placeholder="Tell us about your requirements..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold py-6 text-lg group shadow-lg shadow-cyan-500/20 disabled:opacity-70"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export { ContractManufacturing, ContactSection };